<template>
  <header id="app-header">
    <div class="container header-inner">

      <!-- Brand -->
      <router-link to="/" class="brand" @click="closeMenu">
        <img src="/climblogo.png" alt="CLiMB – Chinese Lutherans in Mission Building" class="brand-logo" />
      </router-link>

      <!-- Desktop nav -->
      <nav class="main-nav" :class="{ open: mobileOpen }">
        <ul class="nav-list">
          <li
            v-for="item in content.navigation?.items"
            :key="item.path"
            class="nav-item"
          >
            <router-link
              :to="item.path"
              :style="{ '--item-color': item.color }"
              @click="closeMenu"
            >
              {{ getNavLabel(item) }}
            </router-link>
          </li>
        </ul>

        <div class="header-actions">
          <div class="lang-group">
            <button
              v-for="lang in languages"
              :key="lang.code"
              @click="setLanguage(lang.code)"
              class="lang-btn"
              :class="{ active: currentLanguage === lang.code }"
            >{{ getLangShort(lang.code) }}</button>
          </div>
          <button class="btn btn-donate">
            {{ currentLanguage === 'en' ? 'Support Us'
               : currentLanguage === 'zh-Hant' ? '支持我們' : '支持我们' }}
          </button>
        </div>
      </nav>

      <!-- Hamburger -->
      <button
        class="hamburger"
        :class="{ open: mobileOpen }"
        @click="mobileOpen = !mobileOpen"
        aria-label="Menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

    </div>

    <!-- Mobile nav backdrop -->
    <div v-if="mobileOpen" class="nav-backdrop" @click="closeMenu"></div>
  </header>
</template>

<script>
import { languageStore } from '../store/language'

export default {
  name: 'HeaderComponent',
  props: {
    content: { type: Object, required: true }
  },
  data() {
    return { mobileOpen: false }
  },
  computed: {
    currentLanguage() { return languageStore.currentLanguage },
    languages()        { return languageStore.languages }
  },
  methods: {
    setLanguage(code) { languageStore.setLanguage(code) },
    closeMenu()       { this.mobileOpen = false },
    getNavLabel(item) {
      if (this.currentLanguage === 'en')      return item.label
      if (this.currentLanguage === 'zh-Hant') return item.labelZhHant || item.labelZh
      return item.labelZhHans || item.labelZh
    },
    getLangShort(code) {
      if (code === 'en')      return 'EN'
      if (code === 'zh-Hant') return '繁'
      return '简'
    }
  }
}
</script>

<style scoped>
/* ── Shell ─────────────────────────────────────────────────── */
.header-inner {
  display: flex;
  align-items: stretch;
  height: 72px;
  gap: 2rem;
}

/* ── Brand ─────────────────────────────────────────────────── */
.brand {
  display: flex;
  align-items: stretch;
  text-decoration: none;
  flex-shrink: 0;
}

.brand-logo {
  height: 100%;
  max-width: 240px;
  width: auto;
  display: block;
  object-fit: contain;
  object-position: left center;
}

/* ── Nav ───────────────────────────────────────────────────── */
.main-nav {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
}

.nav-list {
  display: flex;
  list-style: none;
  gap: .15rem;
  align-items: center;
}

.nav-item a {
  display: block;
  padding: .45rem .85rem;
  font-size: .92rem;
  font-weight: 600;
  color: var(--color-text-light);
  border-radius: 4px;
  text-decoration: none;
  transition: color .18s ease, background .18s ease;
  position: relative;
}

.nav-item a::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: .85rem;
  right: .85rem;
  height: 2px;
  background: var(--item-color, var(--color-primary));
  border-radius: 1px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform .2s ease;
}

.nav-item a:hover {
  color: var(--color-text);
  background: var(--color-background-alt);
}

.nav-item a:hover::after,
.nav-item a.router-link-active::after {
  transform: scaleX(1);
}

.nav-item a.router-link-active {
  color: var(--color-text);
  font-weight: 700;
}

/* ── Header actions (lang + donate) ─────────────────────────── */
.header-actions {
  display: flex;
  align-items: center;
  gap: .75rem;
  flex-shrink: 0;
}

.lang-group {
  display: flex;
  gap: 1px;
  background: var(--color-background-alt);
  border: 1px solid var(--color-border);
  border-radius: 5px;
  padding: 2px;
  overflow: hidden;
}

.lang-btn {
  font-family: var(--font-family);
  font-size: .75rem;
  font-weight: 700;
  padding: .3rem .55rem;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  letter-spacing: .03em;
  transition: background .15s, color .15s;
}

.lang-btn:hover { color: var(--color-text); }

.lang-btn.active {
  background: var(--color-primary);
  color: white;
}

/* Donate */
.btn-donate {
  font-size: .85rem;
  padding: .5rem 1.1rem;
  background: var(--color-fellowship);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 700;
  font-family: var(--font-family);
  cursor: pointer;
  letter-spacing: .02em;
  transition: background .18s, transform .15s;
  white-space: nowrap;
}

.btn-donate:hover {
  background: #9e6a15;
  transform: translateY(-1px);
}

/* ── Hamburger ─────────────────────────────────────────────── */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: auto;
  flex-shrink: 0;
}

.hamburger span {
  display: block;
  height: 2px;
  background: var(--color-text);
  border-radius: 2px;
  transition: transform .25s ease, opacity .2s ease, width .2s ease;
  transform-origin: center;
}

.hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
.hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ── Mobile backdrop ─────────────────────────────────────────── */
.nav-backdrop {
  position: fixed;
  inset: 0;
  top: 76px;
  background: rgba(0,0,0,.25);
  z-index: 150;
}

/* ── Mobile layout ─────────────────────────────────────────── */
@media (max-width: 900px) {
  .hamburger { display: flex; }

  .main-nav {
    position: fixed;
    top: 76px;
    left: 0;
    right: 0;
    z-index: 160;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: #fff;
    border-bottom: 2px solid var(--color-border);
    box-shadow: 0 8px 24px rgba(0,0,0,.12);
    padding: .5rem 1rem 1rem;
    transform: translateY(-110%);
    opacity: 0;
    pointer-events: none;
    transition: transform .28s ease, opacity .22s ease;
  }

  .main-nav.open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: all;
  }

  .nav-list {
    flex-direction: column;
    gap: 0;
    align-items: stretch;
  }

  .nav-item a {
    padding: .75rem 1rem;
    font-size: 1rem;
    border-radius: var(--border-radius);
  }

  .nav-item a::after { display: none; }

  .nav-item a.router-link-active {
    background: var(--color-background-alt);
    color: var(--color-primary);
  }

  .header-actions {
    padding-top: .75rem;
    border-top: 1px solid var(--color-border);
    margin-top: .5rem;
    justify-content: space-between;
  }

  .btn-donate { flex: 1; text-align: center; justify-content: center; }
}

@media (max-width: 480px) {
  .brand-logo { height: 100%; }
}
</style>
